import React, { useState } from 'react';
import {
	Alert,
	Form,
	Popover,
	Switch,
	Tag,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute } from '@fortawesome/pro-duotone-svg-icons/faRoute';
import { Icon } from '@fortawesome/fontawesome-svg-core';
import {
	DEFAULT_SEA_ROUTE_SETTINGS,
	PortActionTypes,
	pickSeaPassageToggles,
	SEA_PASSAGE_TOGGLES,
	SeaPassageToggleKey,
	SeaPassageToggles,
} from '@shared/utils/constants';
import Button from '@client/components/Button';
import styles from '@client/screens/estimates/details/styles/shared.module.css';
import type { LocalPortRotation } from './context/hooks/usePortRotationHandlers';

type Props = {
	portRotation: LocalPortRotation[];
	onSave: (toggles: Partial<SeaPassageToggles>) => void;
	disabled?: boolean;
};

const getLegValues = (portRotation: LocalPortRotation[], key: SeaPassageToggleKey) => portRotation
	.filter((entry) => (
		entry.type !== PortActionTypes.CANAL_TRANSIT &&
		entry.type !== PortActionTypes.BALLAST_LEG
	))
	.map((entry) => entry.routeOptions?.[key] ?? DEFAULT_SEA_ROUTE_SETTINGS[key]);

const SeaPassageTogglesControl = ({
	portRotation,
	onSave,
	disabled,
}: Props) => {
	const [open, setOpen] = useState(false);

	const isMixed = (key: SeaPassageToggleKey) => new Set(getLegValues(portRotation, key)).size > 1;

	const initialValues = Object.fromEntries(SEA_PASSAGE_TOGGLES.map(({ key }) => (
		[key, isMixed(key) ? undefined : getLegValues(portRotation, key)[0] ?? DEFAULT_SEA_ROUTE_SETTINGS[key]]
	)));

	const content = (
		<div className={styles.configContent}>
			<Form
				initialValues={initialValues}
				onFinish={(values) => {
					setOpen(false);
					onSave(pickSeaPassageToggles(values));
				}}
			>
				<Alert
					style={{ marginBottom: 8 }}
					message="These settings apply to all sea passages in the port rotation."
					type="info"
					showIcon
				/>
				{SEA_PASSAGE_TOGGLES.map(({ key, label }) => (
					<Form.Item
						key={key}
						className={styles.configSwitch}
						label={(
							<>
								{label}
								{isMixed(key) && (<Tag className={styles.mixedTag}>Mixed</Tag>)}
							</>
						)}
						name={key}
						valuePropName="checked"
						colon={false}
					>
						<Switch />
					</Form.Item>
				))}
				<Form.Item>
					<div className={styles.configSubmitContainer}>
						<Button
							type="primary"
							htmlType="submit"
							data-testid="sea-passage-toggles-save"
						>
							Apply to all sea passages
						</Button>
					</div>
				</Form.Item>
			</Form>
		</div>
	);

	return (
		<Popover
			title="Sea passage settings for the voyage"
			trigger="click"
			open={open && !disabled}
			onOpenChange={setOpen}
			content={content}
			destroyTooltipOnHide
		>
			<Button
				type="link"
				disabled={disabled}
				data-testid="sea-passage-toggles"
				icon={(<FontAwesomeIcon icon={faRoute as Icon} />)}
			/>
		</Popover>
	);
};

export default SeaPassageTogglesControl;
